<template>
    <div>
        <md-card :class="[{ 'planning-completed': !isActive }, planningItem.planning_item_type]">
            <md-card-header class="header-grid">
                <div class="grid-item">
                    <div class="md-subheading">
                        {{ headerText }} {{ planningItem.planning_item_type_number }}
                        <md-chip
                            v-if="
                                planningItem.approved_quote_ids &&
                                planningItem.planning_item_type === PlanningItemType.lead
                            "
                            class="md-accent quote-sold"
                            style="background-color: #4de473"
                        >
                            Approved
                        </md-chip>
                        <md-chip v-if="calendarItem?.visit_report" class="md-primary">
                            {{ getNameReportState(calendarItem.visit_report.state) }}
                        </md-chip>
                    </div>
                    <div class="md-subhead" v-if="calendarItem">
                        <HourDisplay :date-time="calendarItem.date_time_start" />
                        -
                        <HourDisplay :date-time="calendarItem.date_time_stop" />
                        <star-rating
                            v-if="!!lastQuote"
                            style="display: inline-block; margin-left: 15px"
                            :rating="lastQuote.data.rating ? lastQuote.data.rating.value : 0"
                            :read-only="true"
                            :max-rating="lastQuote.data.rating ? lastQuote.data.rating.max : 5"
                            :show-rating="false"
                            :star-size="10"
                        ></star-rating>
                    </div>
                </div>
            </md-card-header>

            <md-card-content id="planning-card-content" v-if="planningItem.client && executionAddress">
                <div class="md-body-1">
                    {{ planningItem.client.client_data.first_name + ' ' + planningItem.client.client_data.last_name }}
                </div>
                <div class="directions-grid">
                    <div class="md-layout-item md-small-size-20 grid-item">
                        <md-button
                            class="md-icon-button pointer md-raised"
                            @click="openGoogleMaps(executionAddress.address, executionAddress.zip_code)"
                        >
                            <md-icon>directions</md-icon>
                        </md-button>
                    </div>
                    <div class="md-layout-item md-small-size-75 md-caption grid-item">
                        <div>{{ executionAddress.address }}</div>
                        <div>
                            {{ executionAddress.zip_code }}
                            {{ executionAddress.city }}
                        </div>
                    </div>
                </div>
            </md-card-content>

            <md-card-actions>
                <md-badge
                    v-if="
                        planningItem.data.water_meter_stand !== undefined && configuration?.waterStand?.enabled === true
                    "
                    md-content="Ok"
                    class="meter-ok"
                    md-dense
                    style="margin-right: 4px"
                >
                    <md-button class="md-icon-button md-dense md-raised" @click="customWaterStandActive = true">
                        <md-icon>speed</md-icon>
                    </md-button>
                </md-badge>
                <md-button
                    v-if="
                        planningItem.data.water_meter_stand === undefined && configuration?.waterStand?.enabled === true
                    "
                    class="md-icon-button md-dense md-raised"
                    @click="customWaterStandActive = true"
                >
                    <md-icon>speed</md-icon>
                </md-button>

                <md-button
                    @click="upsertWorkOrder(planningItem)"
                    class="md-icon-button md-dense md-raised"
                    v-if="this.isWorkOrderEnabled(planningItem.planning_item_type)"
                >
                    <md-icon>assignment</md-icon>
                </md-button>
                <md-button
                    v-if="planningItem.planning_item_type === PlanningItemType.lead && isActive"
                    @click="createQuote(true)"
                    class="md-raised md-dense"
                    >Offerte
                </md-button>
                <md-button @click="openDetail()" class="md-raised md-dense">Details</md-button>
                <md-button @click="finishPlanning()" class="md-raised md-dense" v-if="isActive">Afwerken</md-button>
            </md-card-actions>
        </md-card>
        <md-dialog-prompt
            :md-active.sync="customWaterStandActive"
            v-model="customWaterStand"
            md-title="Water stand ingeven "
            md-input-placeholder="Water stand"
            md-confirm-text="Save"
            @md-confirm="updateWaterMeterStand"
        />
        <md-dialog :md-active.sync="workOrderPickerActive">
            <md-dialog-title>Werkbonnen</md-dialog-title>
            <md-list>
                <md-list-item
                    v-for="workOrder in linkedWorkOrders"
                    :key="workOrder.work_order_id"
                    @click="openWorkOrder(workOrder)"
                >
                    <md-icon>assignment</md-icon>
                    <span class="md-list-item-text">
                        Werkbon {{ workOrder.work_order_id }}{{ workOrderOwnerLabel(workOrder) }}
                    </span>
                </md-list-item>
            </md-list>
            <md-dialog-actions>
                <md-button @click="workOrderPickerActive = false">Sluiten</md-button>
                <md-button class="md-primary" @click="createNewWorkOrder()">Nieuwe werkbon</md-button>
            </md-dialog-actions>
        </md-dialog>
    </div>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import HourDisplay from '@/components/utils/HourDisplay.vue';
import router from '@/router';
import { cachePlanningDetail } from '@/services/manager/planning.manager';
import {
    ICalendarItemEntity,
    IPlanningItemEntity,
    IQuoteEntity,
    PlanningItemApi,
    PlanningItemReportState,
    PlanningItemType,
    PlanningState,
    WorkOrderApi,
} from '@phrospr/api-backend';
import { getNamePlanningItemType, getNameReportState } from '@phrospr/lib-core/src/engine/planning-item.engine';
import { workOrderEnabledForPlanningType } from '@phrospr/lib-core/src/engine/work-order.engine';
import StarRating from 'vue-star-rating';
import { toastr } from '@/resources/toastr';
import { IConfiguration, IWorkOrderEntity } from '@phrospr/lib-models';

@Component({
    components: { HourDisplay, StarRating },
})
export default class PlanningCard extends Vue {
    @Prop() private planningItem!: IPlanningItemEntity;
    headerText: string;

    public PlanningState = PlanningState;
    public PlanningItemType = PlanningItemType;

    calendarItem: ICalendarItemEntity;
    lastQuote: IQuoteEntity;

    customWaterStand = this.planningItem.data?.water_meter_stand?.toString() ?? '';
    customWaterStandActive = false;

    workOrderPickerActive = false;
    linkedWorkOrders: IWorkOrderEntity[] = [];

    constructor() {
        super();
        this.loadHeaderText();
        this.calendarItem = this.planningItem.selected_calendar_item;
        this.lastQuote = this.planningItem.last_approved_quote_slim;
    }

    get configuration(): Partial<IConfiguration> {
        return this.$store.state.configuration;
    }

    // The visit's own job-site address; older items without one fall back to the client's main
    // address — the technician must navigate to where the work is, not to the billing address.
    get executionAddress() {
        return this.planningItem.data?.delivery_address ?? this.planningItem.client?.client_data?.main_address;
    }

    get isActive() {
        if (!this.planningItem.assigned_calendar_item) return false;
        if (this.planningItem.selected_calendar_item.calendar_item_id !== this.planningItem.assigned_calendar_item_id)
            return false;
        return this.planningItem.state === PlanningState.planned;
    }

    getNameReportState(state: PlanningItemReportState) {
        return getNameReportState(state);
    }

    public async updateWaterMeterStand(value: string) {
        try {
            const water_meter_stand = parseInt(value, 10);
            if (isNaN(water_meter_stand)) {
                Promise.resolve().then(_ => (this.customWaterStandActive = true));
                return toastr.error('ongeldige waterstand');
            }
            try {
                await PlanningItemApi.updatePlanningItemWaterMeterStand(
                    this.planningItem.planning_item_id,
                    water_meter_stand,
                );
                this.reloadPlanning();
            } catch (err) {
                toastr.error('something went wrong');
            }
        } catch (err) {
            toastr.error('invalid number');
        }
    }

    @Emit()
    reloadPlanning() {}

    private loadHeaderText() {
        this.headerText = getNamePlanningItemType(this.planningItem.planning_item_type);
    }

    public openDetail() {
        cachePlanningDetail(this.planningItem);
        router.push({
            path: `/planning/${this.planningItem.selected_calendar_item.calendar_item_id}/detail/planning-item`,
        });
    }

    public finishPlanning() {
        cachePlanningDetail(this.planningItem);
        router.push({ path: `/planning/${this.planningItem.selected_calendar_item.calendar_item_id}/finish` });
    }

    public createQuote(v2 = false) {
        this.$store.commit('setQuoteCreateSettings', {
            client: this.planningItem.client,
            planningItem: this.planningItem,
        });
        if (v2) return router.push({ path: `/quote2/upsert` });
        router.push({ path: `/quote` });
    }

    public async upsertWorkOrder(planningItem: IPlanningItemEntity) {
        if (planningItem.state === PlanningState.planned) {
            return this.createNewWorkOrder();
        }
        try {
            const workOrders: IWorkOrderEntity[] = (
                await WorkOrderApi.getWorkOrdersFromPlanningItem(planningItem.planning_item_id)
            ).data;
            if (workOrders.length === 0) {
                // Finished without a work order: register it after the fact
                // (the backend links it without re-finishing the planning item).
                return this.createNewWorkOrder();
            }
            // One or more work orders exist (e.g. a colleague on the same visit already made
            // theirs): let the technician open an existing one or add their own.
            this.linkedWorkOrders = workOrders;
            this.workOrderPickerActive = true;
        } catch (e) {
            toastr.error('Kon werkbonnen niet ophalen: ' + e.message);
        }
    }

    public createNewWorkOrder() {
        this.workOrderPickerActive = false;
        this.$store.commit('setWorkOrderCreateSettings', {
            client: this.planningItem.client,
            planningItem: this.planningItem,
        });
        return router.push({ path: `/workOrder/upsert` });
    }

    public openWorkOrder(workOrder: IWorkOrderEntity) {
        this.workOrderPickerActive = false;
        return router.push({ path: `/workOrder/upsert/${workOrder.work_order_id}` });
    }

    public workOrderOwnerLabel(workOrder: IWorkOrderEntity): string {
        const user = workOrder.work_order_technician_user ?? workOrder.created_by_user;
        return user ? ` — ${user.first_name ?? ''} ${user.last_name ?? ''}`.trimEnd() : '';
    }

    public openGoogleMaps(address: string, zipCode: string) {
        const urlSuffix = [address] + ', ' + [zipCode];
        window.open('https://www.google.com/maps/search/?api=1&query=' + urlSuffix, '_blank');
    }

    public isWorkOrderEnabled(planning_item_type: PlanningItemType): boolean {
        return workOrderEnabledForPlanningType(this.configuration.workOrders, planning_item_type);
    }
}
</script>

<style scoped>
.planning-completed.md-card {
    background: rgba(0, 0, 0, 0.1);
}

.pointer {
    cursor: pointer;
}

#planning-card-content {
    margin-left: 20px;
}

#planning-card-content-lead {
    margin-left: 20px;
}

.delivery {
    border-left: 3px yellow solid;
    margin: 10px 0px 10px 0px;
}

.lead {
    border-left: 3px #ffa500 solid;
    margin: 10px 0px 10px 0px;
}

.maintenance {
    border-left: 3px #2ea8db solid;
    margin: 10px 0px 10px 0px;
}

.placement {
    border-left: 3px #556b2f solid;
    margin: 10px 0px 10px 0px;
}

.repair {
    border-left: 3px #ff0000 solid;
    margin: 10px 0px 10px 0px;
}

.quote-sold {
    color: white;
    background-color: chartreuse;
    height: 24px;
    line-height: inherit;
    text-align: right;
}

.header-grid {
    display: grid;
    grid-template-columns: 80% 20%;
}

.directions-grid {
    display: grid;
    grid-template-columns: 55px auto;
}

.grid-item {
    display: inline-block;
}
</style>
